import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, Zap } from "lucide-react";

const SEL = "h-10 px-2 bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600 w-full";
const LBL = "text-[11px] font-bold tracking-wide uppercase text-slate-500";

function emptyPosition() {
  return { segment: "OPT", option_type: "CE", side: "SELL", strike_selection: "ATM",
    strike_value: "", premium: "", expiry: "Weekly", lots: 1,
    target_type: "None", target_value: "", sl_type: "None", sl_value: "", trailing: false };
}
function emptyIndicator() {
  return { indicator: "rsi", params: { period: 14 }, operator: ">", compare_to: "value", value: 60, logic: "AND" };
}

function emptyMarketMove() {
  return {
    underlying: "NIFTY",
    reference: "PREVIOUS_CLOSE",
    direction: "UP",
    min_points: 150,
    max_points: 200,
    action: "BUY",
    lots: 1,
    option_type: "PE",
    premium_min: 400,
    premium_max: 450,
    target_type: "Points",
    target_value: "",
    sl_type: "Points",
    sl_value: ""
  };
}

export default function StrategyBuilder() {
  const [meta, setMeta] = useState(null);
  const [name, setName] = useState("");
  const [underlying, setUnderlying] = useState("NIFTY");
  const [capital, setCapital] = useState(100000);
  const [stype, setStype] = useState("Intraday");
  const [productType, setProductType] = useState("MIS");
  const [sentiment, setSentiment] = useState("Neutral");
  const [positions, setPositions] = useState([]);
  const [indicators, setIndicators] = useState([]);
  // EMA touch / multiple EMA condition configuration
  const [emaTouchEnabled, setEmaTouchEnabled] = useState(false);
  const [emaTouchPeriod, setEmaTouchPeriod] = useState(9);
  const [emaTouchDirection, setEmaTouchDirection] = useState("BOTH");

  const [emaApplyOn, setEmaApplyOn] = useState("UNDERLYING");
  const [underlyingEmaTimeframe, setUnderlyingEmaTimeframe] = useState("5m");
  const [optionEmaTimeframe, setOptionEmaTimeframe] = useState("1m");
  const [executionTimeframe, setExecutionTimeframe] = useState("1m");


  const [optionEmaConditions, setOptionEmaConditions] = useState([]);

  const [optionEmaTouchEnabled, setOptionEmaTouchEnabled] = useState(false);
  const [optionEmaTouchPeriod, setOptionEmaTouchPeriod] = useState(9);
  const [optionEmaTouchDirection, setOptionEmaTouchDirection] = useState("BOTH");

  const [emaConditions, setEmaConditions] = useState([
    { fast: 9, condition: "ABOVE", slow: 21 }
  ]);

  // Dynamic option selection based on underlying/index conditions
  const [openingRange, setOpeningRange] = useState({enabled:false,underlying:"NIFTY",timeframe_minutes:5,reference_start:"09:15",reference_end:"09:20",breakout_confirmation:"CLOSE",strike_basis:"ITM1",expiry:"Weekly",lots:1,target_points:10,sl_reference:"OPPOSITE_RANGE",sl_buffer_points:0,execution:"NEXT_CANDLE_OPEN",max_trades:2,reentry_on_opposite_breakout:true});
  const [dynamicOption, setDynamicOption] = useState({
    enabled: false,
    option_type: "CE",
    strike_basis: "ATM",
    expiry: "Weekly",
    selection_mode: "Specific Premium",
    premium: "",
    premium_min: "",
    premium_max: "",
    lots: 1
  });

  const [marketMoves, setMarketMoves] = useState([]);
  const [entryTime, setEntryTime] = useState("09:20");
  const [exitTime, setExitTime] = useState("15:10");
  const [exitOnExpiry, setExitOnExpiry] = useState(false);
  const [days, setDays] = useState(["Mon", "Tue", "Wed", "Thu", "Fri"]);
  const [profitMtm, setProfitMtm] = useState("");
  const [slMtm, setSlMtm] = useState("");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { id: editId } = useParams();

  useEffect(() => { api.get("/strategies/meta").then((r) => setMeta(r.data)); }, []);
  useEffect(() => {
    if (!editId) return;
    api.get(`/strategies/${editId}`).then(({ data }) => {
      setName(data.name || "");
      setUnderlying(data.underlying || "NIFTY");
      setCapital(data.capital ?? 100000);
      setStype(data.strategy_type || "Intraday");
      setProductType(data.product_type || "MIS");
      setSentiment(data.market_sentiment || "Neutral");
      setPositions((data.positions || []).map((p) => ({ ...emptyPosition(), ...p })));
      setIndicators(data.indicators || []);
      setMarketMoves((data.market_moves || []).map((m) => ({ ...emptyMarketMove(), ...m })));
      setEntryTime(data.entry_time || "09:20");
      setExitTime(data.exit_time || "15:10");
      setExitOnExpiry(!!data.exit_on_expiry);
      setDays(data.entry_days || ["Mon", "Tue", "Wed", "Thu", "Fri"]);
      setProfitMtm(data.profit_mtm ?? "");
      setSlMtm(data.stoploss_mtm ?? "");

      // Load saved EMA configuration
      const emaTF = data.ema_timeframes || {};
      setUnderlyingEmaTimeframe(emaTF.underlying || "5m");
      setOptionEmaTimeframe(emaTF.option || "1m");
      setExecutionTimeframe(emaTF.execution || "1m");

      const emaCfg = data.ema_conditions || {};
      setEmaApplyOn(emaCfg.apply_on || "UNDERLYING");

      setEmaConditions(
        Array.isArray(emaCfg.underlying) && emaCfg.underlying.length
          ? emaCfg.underlying.map(x => ({
              fast: Number(x.fast),
              condition: x.condition || "ABOVE",
              slow: Number(x.slow)
            }))
          : [{ fast: 9, condition: "ABOVE", slow: 21 }]
      );

      setOptionEmaConditions(
        Array.isArray(emaCfg.option) 
          ? emaCfg.option.map(x => ({
              fast: Number(x.fast),
              condition: x.condition || "ABOVE",
              slow: Number(x.slow)
            }))
          : []
      );

      const emaTouchCfg = data.ema_touch || {};
      setEmaTouchEnabled(!!emaTouchCfg.enabled);
      setEmaTouchPeriod(Number(emaTouchCfg.ema_period || 9));
      setEmaTouchDirection(emaTouchCfg.direction || "BOTH");

      const optionEmaTouchCfg = data.option_ema_touch || {};
      setOptionEmaTouchEnabled(!!optionEmaTouchCfg.enabled);
      setOptionEmaTouchPeriod(Number(optionEmaTouchCfg.ema_period || 9));
      setOptionEmaTouchDirection(optionEmaTouchCfg.direction || "BOTH");

      const dynamicCfg = data.dynamic_option_selection || {};
      setDynamicOption(prev => ({
        ...prev,
        ...dynamicCfg,
        premium: dynamicCfg.premium ?? "",
        premium_min: dynamicCfg.premium_min ?? "",
        premium_max: dynamicCfg.premium_max ?? "",
        lots: Number(dynamicCfg.lots || 1)
      }));

      setOpeningRange({
        enabled: !!data.opening_range_breakout?.enabled,
        underlying: data.opening_range_breakout?.underlying || data.underlying || "NIFTY",
        timeframe_minutes: Number(data.opening_range_breakout?.timeframe_minutes || 5),
        reference_start: data.opening_range_breakout?.reference_start || "09:15",
        reference_end: data.opening_range_breakout?.reference_end ||
          (Number(data.opening_range_breakout?.timeframe_minutes || 5) === 30 ? "09:45" : "09:20"),
        breakout_confirmation: data.opening_range_breakout?.breakout_confirmation || "CLOSE",
        strike_basis: data.opening_range_breakout?.strike_basis || "ITM1",
        expiry: data.opening_range_breakout?.expiry || "Weekly",
        lots: Number(data.opening_range_breakout?.lots || 1),
        target_points: Number(data.opening_range_breakout?.target_points || 10),
        sl_reference: data.opening_range_breakout?.sl_reference || "OPPOSITE_RANGE",
        sl_buffer_points: Number(data.opening_range_breakout?.sl_buffer_points || 0),
        execution: data.opening_range_breakout?.execution || "NEXT_CANDLE_OPEN",
        max_trades: Number(data.opening_range_breakout?.max_trades || 2),
        reentry_on_opposite_breakout:
          data.opening_range_breakout?.reentry_on_opposite_breakout !== false
      });

    }).catch(() => toast.error("Could not load strategy"));
  }, [editId]);
  useEffect(() => {
    if (!underlying) return;
    api.get(`/strategies/quote/${underlying}`).then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [underlying]);

  const applyTemplate = (tid) => {
    const t = meta.option_templates.find((x) => x.id === tid);
    if (!t) return;
    setSentiment(t.sentiment);
    setPositions(t.legs.map((l) => ({ ...emptyPosition(), option_type: l.opt, side: l.side, strike_selection: l.strike })));
    if (!name) setName(t.name + " - " + underlying);
    toast.success(`Loaded template: ${t.name}`);
  };

  const setPos = (i, k, v) => setPositions((p) => p.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const setInd = (i, k, v) => setIndicators((p) => p.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const setMarketMove = (i, k, v) => setMarketMoves((p) => p.map((x, idx) => idx === i ? { ...x, [k]: v } : x));

  const toggleDay = (d) => setDays((ds) => ds.includes(d) ? ds.filter((x) => x !== d) : [...ds, d]);

  const save = async (deploy = false) => {
    if (!name.trim()) { toast.error("Give your strategy a name"); return; }
    setBusy(true);
    const payload = {
      name, underlying, capital: Number(capital), strategy_type: stype, product_type: productType, market_sentiment: sentiment,
      positions: positions.map((p) => ({ ...p, lots: Number(p.lots) || 1,
        strike_value: p.strike_value ? Number(p.strike_value) : null,
        premium: p.premium ? Number(p.premium) : null,
        target_value: p.target_value ? Number(p.target_value) : null,
        sl_value: p.sl_value ? Number(p.sl_value) : null })),
      indicators,
          ema_timeframes: {
            underlying: underlyingEmaTimeframe || "5m",
            option: optionEmaTimeframe || "1m",
            execution: executionTimeframe || "1m"
          },
      ema_touch: {
            enabled: emaTouchEnabled,
            ema_period: Number(emaTouchPeriod),
            direction: emaTouchDirection
          },
          option_ema_touch: {
            enabled: !!optionEmaTouchEnabled,
            ema_period: Number(optionEmaTouchPeriod || 9),
            direction: optionEmaTouchDirection || "BOTH"
          },
          ema_conditions: {
            apply_on: emaApplyOn || "UNDERLYING",
            underlying: (emaConditions || []).map(x => ({
              fast: Number(x.fast),
              condition: x.condition,
              slow: Number(x.slow)
            })),
            option: (optionEmaConditions || []).map(x => ({
              fast: Number(x.fast),
              condition: x.condition,
              slow: Number(x.slow)
            }))
          },
          opening_range_breakout: {...openingRange,enabled:!!openingRange.enabled,timeframe_minutes:Number(openingRange.timeframe_minutes||5),lots:Number(openingRange.lots||1),target_points:Number(openingRange.target_points||10),sl_buffer_points:Number(openingRange.sl_buffer_points||0),max_trades:Number(openingRange.max_trades||2)},
          dynamic_option_selection: {
            ...dynamicOption,
            lots: Number(dynamicOption.lots || 1),
            premium: dynamicOption.premium === "" ? "" : Number(dynamicOption.premium),
            premium_min: dynamicOption.premium_min === "" ? "" : Number(dynamicOption.premium_min),
            premium_max: dynamicOption.premium_max === "" ? "" : Number(dynamicOption.premium_max)
          },
          market_moves: marketMoves.map((m) => ({
        ...m,
        min_points: Number(m.min_points) || 0,
        max_points: Number(m.max_points) || 0,
        lots: Number(m.lots) || 1,
        premium_min: Number(m.premium_min) || 0,
        premium_max: Number(m.premium_max) || 0,
        target_value: m.target_value ? Number(m.target_value) : null,
        sl_value: m.sl_value ? Number(m.sl_value) : null,
      })),
      entry_time: entryTime,
      exit_time: exitTime,
      exit_on_expiry: !!exitOnExpiry,
      entry_days: days,
      profit_mtm: profitMtm ? Number(profitMtm) : null,
      stoploss_mtm: slMtm ? Number(slMtm) : null,
    };
    try {
      const { data } = editId
        ? await api.put(`/strategies/${editId}`, payload)
        : await api.post("/strategies", payload);
      toast.success(editId ? "Strategy updated" : "Strategy saved");
      nav(deploy ? "/autotrade" : `/backtest?strategy=${data.id || editId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  if (!meta) return <Layout><div className="p-8 text-slate-400">Loading…</div></Layout>;

  const showPremium = (s) => s.startsWith("PREMIUM");

  return (
    <Layout>
      <PageHeader title="Strategy Builder" subtitle="Build, review and save your trading strategy using connected entry, option, risk and exit parameters."
        right={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <button data-testid="save-strategy" onClick={() => save(false)} disabled={busy}
              className="h-10 w-full justify-center px-3 border border-slate-900 text-sm font-semibold flex items-center gap-2 hover:bg-slate-900 hover:text-white transition-colors sm:w-auto sm:px-4">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save & Backtest
            </button>
            <button data-testid="save-deploy" onClick={() => save(true)} disabled={busy}
              className="h-10 w-full justify-center px-3 bg-blue-600 text-white text-sm font-semibold flex items-center gap-2 hover:bg-blue-700 transition-colors sm:w-auto sm:px-4">
              <Zap className="w-4 h-4" /> Save & Deploy
            </button>
          </div>
        } />

      <div className="grid lg:grid-cols-4 gap-0 border-b border-slate-200">
        {/* Left / main */}
        <div className="lg:col-span-3 border-r border-slate-200">
          {/* Basics */}
          <section className="p-6 border-b border-slate-200 bg-white">
            <h3 className="font-heading font-bold mb-1">1. Strategy & Market</h3><p className="text-xs text-slate-500 mb-4">Define the strategy identity, underlying market and capital.</p>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <label className={LBL}>Strategy Name</label>
                <input data-testid="strategy-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Nifty Short Straddle"
                  className="mt-1 w-full h-10 px-3 bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600" />
              </div>
              <div>
                <label className={LBL}>Underlying</label>
                <select data-testid="underlying-select" value={underlying} onChange={(e) => setUnderlying(e.target.value)} className={`${SEL} mt-1`}>
                  {meta.instruments.map((i) => <option key={i.symbol} value={i.symbol}>{i.symbol}</option>)}
                </select>
              </div>
              <div>
                <label className={LBL}>Capital (₹)</label>
                <input data-testid="capital-input" type="number" value={capital} onChange={(e) => setCapital(e.target.value)}
                  className="mt-1 w-full h-10 px-3 font-mono bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600" />
              </div>
              <div>
                <label className={LBL}>Type</label>
                <select value={stype} onChange={(e) => setStype(e.target.value)} className={`${SEL} mt-1`}>
                  <option>Intraday</option><option>Positional</option>
                </select>
              </div>
              <div>
                <label className={LBL}>Product Type</label>
                <select value={productType} onChange={(e) => setProductType(e.target.value)} className={`${SEL} mt-1`}>
                  <option value="MIS">MIS / Intraday</option>
                  <option value="NRML">Normal / Overnight (NRML)</option>
                </select>
              </div>
              <div>
                <label className={LBL}>Market Sentiment</label>
                <select data-testid="sentiment-select" value={sentiment} onChange={(e) => setSentiment(e.target.value)} className={`${SEL} mt-1`}>
                  {meta.market_sentiments.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className={LBL}>Option Templates</label>
                <select data-testid="template-select" onChange={(e) => e.target.value && applyTemplate(e.target.value)} className={`${SEL} mt-1`} defaultValue="">
                  <option value="">Load a pre-built strategy…</option>
                  {meta.option_templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.sentiment})</option>)}
                </select>
              </div>
            </div>
          </section>


          <section className="p-6 bg-white border-b border-slate-200">
            <div className="mb-4">
              <h3 className="font-heading font-bold mb-1">
                2. Entry Timing
              </h3>
              <p className="text-xs text-slate-500">
                Configure when the strategy is allowed to enter and the timeframes used for execution and EMA calculations.
              </p>
            </div>

            {/* =====================================================
                     EMA TIMEFRAMES
                   ===================================================== */}

                <div className="mb-5 border border-slate-200 bg-slate-50 p-4">

                  <div className="font-heading font-bold text-sm mb-3">
                    EMA & Entry Timeframes
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                    <div>

                      <label className="text-[10px] uppercase font-bold text-slate-500">
                        Underlying EMA Timeframe
                      </label>

                      <select
                        value={underlyingEmaTimeframe}
                        onChange={e =>
                          setUnderlyingEmaTimeframe(e.target.value)
                        }
                        className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
                      >

                        <option value="1m">1 Minute</option>
                        <option value="5m">5 Minutes</option>
                        <option value="15m">15 Minutes</option>

                      </select>

                      <div className="text-[10px] text-slate-500 mt-1">
                        Timeframe used to calculate index EMA.
                      </div>

                    </div>

                    <div>

                      <label className="text-[10px] uppercase font-bold text-slate-500">
                        Option EMA Timeframe
                      </label>

                      <select
                        value={optionEmaTimeframe}
                        onChange={e =>
                          setOptionEmaTimeframe(e.target.value)
                        }
                        className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
                      >

                        <option value="1m">1 Minute</option>
                        <option value="5m">5 Minutes</option>
                        <option value="15m">15 Minutes</option>

                      </select>

                      <div className="text-[10px] text-slate-500 mt-1">
                        Timeframe used to calculate option EMA.
                      </div>

                    </div>

                    <div>

                      <label className="text-[10px] uppercase font-bold text-slate-500">
                        Entry Execution Timeframe
                      </label>

                      <select
                        value={executionTimeframe}
                        onChange={e =>
                          setExecutionTimeframe(e.target.value)
                        }
                        className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
                      >

                        <option value="1m">1 Minute</option>
                        <option value="5m">5 Minutes</option>
                        <option value="15m">15 Minutes</option>

                      </select>

                      <div className="text-[10px] text-slate-500 mt-1">
                        Timeframe used for backtest entry execution.
                      </div>

                    </div>

                  </div>

                  <div className="mt-3 text-xs text-slate-600">
                    EMA timeframe and execution timeframe are independent.
                    Example: calculate NIFTY EMA on 5-minute candles and
                    execute the option entry on the next 1-minute candle.
                  </div>

                </div>

            <div>
              <h3 className="font-heading font-bold mb-1">2. Entry Timing</h3>
              <div className="flex gap-4 items-end mb-4">
                <div><label className={LBL}>Entry Time</label>
                  <input type="time" value={entryTime} onChange={(e) => setEntryTime(e.target.value)}
                    className="mt-1 h-10 px-2 font-mono bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600" /></div>
              </div>
              <label className={LBL}>Enter on Days</label>
              <div className="flex gap-1 mt-2">
                {meta.days.map((d) => (
                  <button key={d} onClick={() => toggleDay(d)} data-testid={`day-${d}`}
                    className={`px-3 h-9 text-xs font-semibold border transition-colors ${days.includes(d) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-300"}`}>{d}</button>
                ))}
              </div>
            </div>
          </section>


          {/* Indicators */}
          <section className="p-6 border-b border-slate-200 bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold">3. Entry Conditions — Indicators</h3>
              <button data-testid="add-indicator" onClick={() => setIndicators([...indicators, emptyIndicator()])}
                className="text-xs font-semibold text-blue-600 flex items-center gap-1 hover:underline"><Plus className="w-3 h-3" /> Add Condition</button>
            </div>
            {indicators.length === 0 && <p className="text-sm text-slate-400">No indicator filters. Add RSI, MACD, Supertrend, EMA, Bollinger and more in combination.</p>}
            <div className="space-y-2">
              {indicators.map((ind, i) => (
                <div key={i} className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end border border-slate-200 p-3" data-testid={`indicator-${i}`}>
                  {i > 0 && <div><label className={LBL}>Logic</label>
                    <select value={ind.logic} onChange={(e) => setInd(i, "logic", e.target.value)} className={`${SEL} mt-1`}><option>AND</option><option>OR</option></select></div>}
                  <div><label className={LBL}>Indicator</label>
                    <select value={ind.indicator} onChange={(e) => setInd(i, "indicator", e.target.value)} className={`${SEL} mt-1`}>
                      {meta.indicators.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                    </select></div>
                  <div><label className={LBL}>Operator</label>
                    <select value={ind.operator} onChange={(e) => setInd(i, "operator", e.target.value)} className={`${SEL} mt-1`}>
                      <option value=">">&gt;</option><option value="<">&lt;</option>
                      <option value="crosses_above">Crosses Above</option><option value="crosses_below">Crosses Below</option>
                    </select></div>
                  <div><label className={LBL}>Value</label>
                    <input value={ind.value} onChange={(e) => setInd(i, "value", Number(e.target.value))}
                      className="mt-1 w-full h-10 px-2 font-mono bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600" /></div>
                  <button onClick={() => setIndicators(indicators.filter((_, idx) => idx !== i))}
                    className="h-10 w-9 flex items-center justify-center text-rose-500 hover:bg-rose-50 border border-slate-300"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </section>

          {/* Market Move Conditions */}
          <section className="p-6 border-b border-slate-200 bg-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading font-bold">3. Entry Conditions — Market Move</h3>
                <p className="text-xs text-slate-400 mt-1">
                  Trigger an option entry when the underlying moves within a defined point range.
                </p>
              </div>
              <button
                data-testid="add-market-move"
                onClick={() => setMarketMoves([...marketMoves, emptyMarketMove()])}
                className="text-xs font-semibold text-blue-600 flex items-center gap-1 hover:underline"
              >
                <Plus className="w-3 h-3" /> Add Condition
              </button>
            </div>

            {marketMoves.length === 0 && (
              <p className="text-sm text-slate-400">
                No market-move filters. Add one to trigger BUY PE/CE based on NIFTY movement.
              </p>
            )}

            <div className="space-y-3">
              {marketMoves.map((move, i) => (
                <div
                  key={i}
                  className="border border-slate-200 p-3"
                  data-testid={`market-move-${i}`}
                >
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">

                    {i > 0 && (
                      <div>
                        <label className={LBL}>Logic</label>
                        <select
                          value={move.logic || "AND"}
                          onChange={(e) => setMarketMove(i, "logic", e.target.value)}
                          className={`${SEL} mt-1`}
                        >
                          <option>AND</option>
                          <option>OR</option>
                        </select>
                      
              <button
                type="button"
                onClick={() => setMarketMoves((p) => p.filter((_, index) => index !== i))}
                className="h-9 px-3 text-xs font-semibold text-red-600 border border-red-200 rounded-sm hover:bg-red-50"
                title="Delete this leg"
              >
                🗑 Delete
              </button>
</div>
                    )}

                    <div>
                      <label className={LBL}>Underlying</label>
                      <select
                        value={move.underlying}
                        onChange={(e) => setMarketMove(i, "underlying", e.target.value)}
                        className={`${SEL} mt-1`}
                      >
                        {meta.instruments.map((x) => (
                          <option key={x.symbol} value={x.symbol}>{x.symbol}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={LBL}>Reference</label>
                      <select
                        value={move.reference}
                        onChange={(e) => setMarketMove(i, "reference", e.target.value)}
                        className={`${SEL} mt-1`}
                      >
                        <option value="PREVIOUS_CLOSE">Previous Close</option>
                        <option value="OPEN">Day Open</option>
                      </select>
                    </div>

                    <div>
                      <label className={LBL}>Direction</label>
                      <select
                        value={move.direction}
                        onChange={(e) => setMarketMove(i, "direction", e.target.value)}
                        className={`${SEL} mt-1`}
                      >
                        <option value="UP">UP (+)</option>
                        <option value="DOWN">DOWN (-)</option>
                      </select>
                    </div>

                    <div>
                      <label className={LBL}>Min Move (Points)</label>
                      <input
                        type="number"
                        value={move.min_points}
                        onChange={(e) => setMarketMove(i, "min_points", e.target.value)}
                        className="mt-1 w-full h-10 px-2 font-mono bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </div>

                    <div>
                      <label className={LBL}>Max Move (Points)</label>
                      <input
                        type="number"
                        value={move.max_points}
                        onChange={(e) => setMarketMove(i, "max_points", e.target.value)}
                        className="mt-1 w-full h-10 px-2 font-mono bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </div>

                    <div>
                      <label className={LBL}>Action</label>
                      <select
                        value={move.action}
                        onChange={(e) => setMarketMove(i, "action", e.target.value)}
                        className={`${SEL} mt-1`}
                      >
                        <option value="BUY">BUY</option>
                        <option value="SELL">SELL</option>
                      </select>
                    </div>

                    <div>
                      <label className={LBL}>Lots</label>
                      <input
                        type="number"
                        min="1"
                        value={move.lots}
                        onChange={(e) => setMarketMove(i, "lots", e.target.value)}
                        className="mt-1 w-full h-10 px-2 font-mono bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </div>

                    <div>
                      <label className={LBL}>Option Type</label>
                      <select
                        value={move.option_type}
                        onChange={(e) => setMarketMove(i, "option_type", e.target.value)}
                        className={`${SEL} mt-1`}
                      >
                        <option value="CE">CE</option>
                        <option value="PE">PE</option>
                      </select>
                    </div>

                    <div>
                      <label className={LBL}>Premium Min</label>
                      <input
                        type="number"
                        value={move.premium_min}
                        onChange={(e) => setMarketMove(i, "premium_min", e.target.value)}
                        className="mt-1 w-full h-10 px-2 font-mono bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </div>

                    <div>
                      <label className={LBL}>Premium Max</label>
                      <input
                        type="number"
                        value={move.premium_max}
                        onChange={(e) => setMarketMove(i, "premium_max", e.target.value)}
                        className="mt-1 w-full h-10 px-2 font-mono bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </div>

                    <div>
                      <label className={LBL}>Target Type</label>
                      <select
                        value={move.target_type}
                        onChange={(e) => setMarketMove(i, "target_type", e.target.value)}
                        className={`${SEL} mt-1`}
                      >
                        <option value="None">None</option>
                        <option value="Points">Points</option>
                        <option value="% of Entry">% of Entry</option>
                      </select>
                    </div>

                    <div>
                      <label className={LBL}>Target</label>
                      <input
                        type="number"
                        value={move.target_value}
                        onChange={(e) => setMarketMove(i, "target_value", e.target.value)}
                        placeholder="e.g. 50"
                        className="mt-1 w-full h-10 px-2 font-mono bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </div>

                    <div>
                      <label className={LBL}>Stop Loss Type</label>
                      <select
                        value={move.sl_type}
                        onChange={(e) => setMarketMove(i, "sl_type", e.target.value)}
                        className={`${SEL} mt-1`}
                      >
                        <option value="None">None</option>
                        <option value="Points">Points</option>
                        <option value="% of Entry">% of Entry</option>
                      </select>
                    </div>

                    <div>
                      <label className={LBL}>Stop Loss</label>
                      <input
                        type="number"
                        value={move.sl_value}
                        onChange={(e) => setMarketMove(i, "sl_value", e.target.value)}
                        placeholder="e.g. 25"
                        className="mt-1 w-full h-10 px-2 font-mono bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </div>

                    <div className="flex items-end">
                      <button
                        onClick={() => setMarketMoves(marketMoves.filter((_, idx) => idx !== i))}
                        className="h-10 w-9 flex items-center justify-center text-rose-500 hover:bg-rose-50 border border-slate-300"
                        title="Remove condition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                  </div>
                </div>
              ))}
            </div>
          </section>


          <section className="p-6 bg-white border-b border-slate-200">
            <div className="mb-4">
              <h3 className="font-heading font-bold mb-1">
                3. Entry Conditions — EMA
              </h3>
              <p className="text-xs text-slate-500">
                Configure EMA direction, EMA touch, option EMA and Opening Range Breakout conditions.
              </p>
            </div>

            {/* =========================================================
                 3. ENTRY CONDITIONS — EMA
               ========================================================= */}
             <div className="mb-4 px-1">
               <div className="text-[11px] font-bold uppercase tracking-wide text-blue-700">
                 3. Entry Conditions — EMA
               </div>
               <div className="text-xs text-slate-500 mt-1">
                 Configure underlying / option EMA conditions and EMA-touch entries.
               </div>
             </div>
            <div className="mt-6 border border-blue-200 bg-white">

              {/* Header */}
              <div className="px-5 py-4 border-b border-blue-200 bg-blue-50">

                <div className="font-heading font-bold text-base">
                  EMA Conditions
                </div>

                <div className="text-xs text-slate-500 mt-1">
                  Apply EMA conditions to the underlying index,
                  the option, or both.
                </div>

              </div>

              <div className="p-5">

                {/* =====================================================
                     APPLY EMA ON
                   ===================================================== */}

                <div className="mb-5">

                  <label className="text-[10px] uppercase font-bold text-slate-500">
                    Apply EMA Conditions On
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">

                    {[
                      ["UNDERLYING", "Underlying / Index"],
                      ["OPTION", "Option"],
                      ["BOTH", "Both"]
                    ].map(([value, label]) => (

                      <button
                        key={value}
                        type="button"
                        onClick={() => setEmaApplyOn(value)}
                        className={`p-3 border text-left ${
                          emaApplyOn === value
                            ? "border-blue-600 bg-blue-50 text-blue-700"
                            : "border-slate-300 bg-white text-slate-700"
                        }`}
                      >

                        <div className="font-semibold text-sm">
                          {label}
                        </div>

                        <div className="text-[11px] text-slate-500 mt-1">

                          {value === "UNDERLYING"
                            ? "EMA calculated from NIFTY / index"
                            : value === "OPTION"
                            ? "EMA calculated from selected CE / PE"
                            : "Underlying and option must both pass"}

                        </div>

                      </button>

                    ))}

                  </div>

                </div>
{/* =====================================================
                     UNDERLYING EMA
                   ===================================================== */}

                {(emaApplyOn === "UNDERLYING" ||
                  emaApplyOn === "BOTH") && (

                  <div className="border border-slate-200 p-4 mb-5">

                    <div className="flex items-center justify-between mb-3">

                      <div>

                        <div className="font-heading font-bold text-sm">
                          EMA Conditions — Underlying Index
                        </div>

                        <div className="text-xs text-slate-500 mt-1">
                          These conditions are evaluated on the main index.
                        </div>

                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          setEmaConditions([
                            ...(emaConditions || []),
                            {
                              fast: 9,
                              condition: "ABOVE",
                              slow: 21
                            }
                          ])
                        }
                        className="text-xs font-semibold text-blue-600"
                      >
                        + Add EMA Condition
                      </button>

                    </div>

                    <div className="space-y-3">

                      {(emaConditions || []).map((ec, i) => (

                        <div
                          key={i}
                          className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr_1fr_auto] gap-3 items-end border border-slate-200 p-3"
                        >

                          <div>

                            <label className="text-[10px] uppercase font-bold text-slate-500">
                              EMA
                            </label>

                            <select
                              value={ec.fast}
                              onChange={e => {
                                const next = [...emaConditions];

                                next[i] = {
                                  ...next[i],
                                  fast: Number(e.target.value)
                                };

                                setEmaConditions(next);
                              }}
                              className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
                            >

                              {[5,9,13,20,21,34,50,55,100,200].map(v => (
                                <option key={v} value={v}>
                                  EMA {v}
                                </option>
                              ))}

                            </select>

                          </div>

                          <div>

                            <label className="text-[10px] uppercase font-bold text-slate-500">
                              Condition
                            </label>

                            <select
                              value={ec.condition}
                              onChange={e => {
                                const next = [...emaConditions];

                                next[i] = {
                                  ...next[i],
                                  condition: e.target.value
                                };

                                setEmaConditions(next);
                              }}
                              className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
                            >

                              <option value="ABOVE">
                                Is Above
                              </option>

                              <option value="BELOW">
                                Is Below
                              </option>

                              <option value="CROSS_ABOVE">
                                Crosses Above
                              </option>

                              <option value="CROSS_BELOW">
                                Crosses Below
                              </option>

                            </select>

                          </div>

                          <div>

                            <label className="text-[10px] uppercase font-bold text-slate-500">
                              Compared With
                            </label>

                            <select
                              value={ec.slow}
                              onChange={e => {
                                const next = [...emaConditions];

                                next[i] = {
                                  ...next[i],
                                  slow: Number(e.target.value)
                                };

                                setEmaConditions(next);
                              }}
                              className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
                            >

                              {[5,9,13,20,21,34,50,55,100,200].map(v => (
                                <option key={v} value={v}>
                                  EMA {v}
                                </option>
                              ))}

                            </select>

                          </div>

                          <button
                            type="button"
                            onClick={() => {

                              const next =
                                emaConditions.filter(
                                  (_, j) => j !== i
                                );

                              setEmaConditions(
                                next.length
                                  ? next
                                  : [{
                                      fast: 9,
                                      condition: "ABOVE",
                                      slow: 21
                                    }]
                              );

                            }}
                            className="h-10 px-3 border border-red-200 text-red-500 text-xs"
                          >
                            Remove
                          </button>

                        </div>

                      ))}

                    </div>

                    {/* UNDERLYING TOUCH */}

                    <div className="mt-5 border border-amber-200 bg-amber-50 p-4">

                      <div className="flex items-center gap-3">

                        <input
                          type="checkbox"
                          checked={emaTouchEnabled}
                          onChange={e =>
                            setEmaTouchEnabled(
                              e.target.checked
                            )
                          }
                        />

                        <div>

                          <div className="font-semibold text-sm">
                            Entry on Touch of Underlying EMA
                          </div>

                          <div className="text-xs text-slate-500 mt-1">
                            Entry is triggered when the underlying
                            index price touches the selected EMA.
                          </div>

                        </div>

                      </div>

                      {emaTouchEnabled && (

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">

                          <div>

                            <label className="text-[10px] uppercase font-bold text-slate-500">
                              Select EMA
                            </label>

                            <select
                              value={emaTouchPeriod}
                              onChange={e =>
                                setEmaTouchPeriod(
                                  Number(e.target.value)
                                )
                              }
                              className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
                            >

                              {[5,9,13,20,21,34,50,55,100,200].map(v => (
                                <option key={v} value={v}>
                                  EMA {v}
                                </option>
                              ))}

                            </select>

                          </div>

                          <div>

                            <label className="text-[10px] uppercase font-bold text-slate-500">
                              Touch Direction
                            </label>

                            <select
                              value={emaTouchDirection}
                              onChange={e =>
                                setEmaTouchDirection(
                                  e.target.value
                                )
                              }
                              className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
                            >

                              <option value="BOTH">
                                Touch from Either Side
                              </option>

                              <option value="FROM_ABOVE">
                                Price Touching EMA from Above
                              </option>

                              <option value="FROM_BELOW">
                                Price Touching EMA from Below
                              </option>

                            </select>

                          </div>

                        </div>

                      )}

                    </div>

                  </div>

                )}

                <div className="mt-6 border border-amber-200 bg-white">
                  <div className="px-5 py-2 border-b border-amber-200 bg-amber-50">
                    <div className="text-[11px] font-bold uppercase tracking-wide text-amber-700">
                      3. Entry Conditions — Opening Range Breakout
                    </div>
                  </div>
                  <div className="px-5 py-4 border-b border-amber-200 bg-amber-50"><div className="flex items-center justify-between"><div><div className="font-heading font-bold text-base">Opening Range Breakout — First Candle</div><div className="text-xs text-slate-500 mt-1">09:15 first 5m/30m candle. Close above High buys CE; close below Low buys PE.</div></div><label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={!!openingRange.enabled} onChange={e=>setOpeningRange(x=>({...x,enabled:e.target.checked}))}/> Enable</label></div></div><div className="p-5 grid md:grid-cols-3 gap-4"><div><label className="text-[10px] uppercase font-bold text-slate-500">Index</label><select value={openingRange.underlying} onChange={e=>setOpeningRange(x=>({...x,underlying:e.target.value}))} className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"><option value="NIFTY">NIFTY</option><option value="SENSEX">SENSEX</option></select></div><div><label className="text-[10px] uppercase font-bold text-slate-500">Timeframe</label><select value={openingRange.timeframe_minutes} onChange={e=>{const v=Number(e.target.value);setOpeningRange(x=>({...x,timeframe_minutes:v,reference_end:v===30?"09:45":"09:20"}))}} className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"><option value={5}>5m — 09:15–09:20</option><option value={30}>30m — 09:15–09:45</option></select></div><div><label className="text-[10px] uppercase font-bold text-slate-500">Option Strike</label><select value={openingRange.strike_basis} onChange={e=>setOpeningRange(x=>({...x,strike_basis:e.target.value}))} className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"><option value="ATM">ATM</option><option value="ITM1">1 ITM</option><option value="ITM2">2 ITM</option><option value="ITM3">3 ITM</option><option value="ITM4">4 ITM</option><option value="ITM5">5 ITM</option></select></div><div><label className="text-[10px] uppercase font-bold text-slate-500">Expiry</label><select value={openingRange.expiry} onChange={e=>setOpeningRange(x=>({...x,expiry:e.target.value}))} className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"><option>Weekly</option><option>Monthly</option></select></div><div><label className="text-[10px] uppercase font-bold text-slate-500">Lots</label><input type="number" min="1" value={openingRange.lots} onChange={e=>setOpeningRange(x=>({...x,lots:Number(e.target.value||1)}))} className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm"/></div><div><label className="text-[10px] uppercase font-bold text-slate-500">Target — Option Points</label><input type="number" min="0.05" step="0.05" value={openingRange.target_points} onChange={e=>setOpeningRange(x=>({...x,target_points:Number(e.target.value||10)}))} className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm"/></div><div><label className="text-[10px] uppercase font-bold text-slate-500">SL Buffer — Index Points</label><input type="number" min="0" step="1" value={openingRange.sl_buffer_points} onChange={e=>setOpeningRange(x=>({...x,sl_buffer_points:Number(e.target.value||0)}))} className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm"/></div><div><label className="text-[10px] uppercase font-bold text-slate-500">Max Trades / Day</label><input type="number" min="1" max="10" value={openingRange.max_trades} onChange={e=>setOpeningRange(x=>({...x,max_trades:Number(e.target.value||2)}))} className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm"/></div><label className="flex items-center gap-2 text-sm font-semibold self-end pb-2"><input type="checkbox" checked={openingRange.reentry_on_opposite_breakout!==false} onChange={e=>setOpeningRange(x=>({...x,reentry_on_opposite_breakout:e.target.checked}))}/> Re-enter after SL only on opposite breakout</label></div><div className="px-5 pb-5 text-xs text-slate-500">Close above first-candle High → CE. Close below first-candle Low → PE. Target is option-premium points; SL is the opposite first-candle boundary.</div></div>
                {/* =====================================================
                     OPTION EMA
                   ===================================================== */}

                {(emaApplyOn === "OPTION" ||
                  emaApplyOn === "BOTH") && (

                  <div className="border border-purple-200 p-4">

                    <div className="flex items-center justify-between mb-3">

                      <div>

                        <div className="font-heading font-bold text-sm">
                          EMA Conditions — Option
                        </div>

                        <div className="text-xs text-slate-500 mt-1">
                          These conditions are calculated from the
                          actual historical CE / PE selected for the trade.
                        </div>

                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          setOptionEmaConditions([
                            ...(optionEmaConditions || []),
                            {
                              fast: 9,
                              condition: "ABOVE",
                              slow: 21
                            }
                          ])
                        }
                        className="text-xs font-semibold text-purple-600"
                      >
                        + Add Option EMA Condition
                      </button>

                    </div>

                    <div className="space-y-3">

                      {(optionEmaConditions || []).map((ec, i) => (

                        <div
                          key={i}
                          className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr_1fr_auto] gap-3 items-end border border-slate-200 p-3"
                        >

                          <div>

                            <label className="text-[10px] uppercase font-bold text-slate-500">
                              EMA
                            </label>

                            <select
                              value={ec.fast}
                              onChange={e => {

                                const next =
                                  [...optionEmaConditions];

                                next[i] = {
                                  ...next[i],
                                  fast: Number(e.target.value)
                                };

                                setOptionEmaConditions(next);

                              }}
                              className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
                            >

                              {[5,9,13,20,21,34,50,55,100,200].map(v => (
                                <option key={v} value={v}>
                                  EMA {v}
                                </option>
                              ))}

                            </select>

                          </div>

                          <div>

                            <label className="text-[10px] uppercase font-bold text-slate-500">
                              Condition
                            </label>

                            <select
                              value={ec.condition}
                              onChange={e => {

                                const next =
                                  [...optionEmaConditions];

                                next[i] = {
                                  ...next[i],
                                  condition: e.target.value
                                };

                                setOptionEmaConditions(next);

                              }}
                              className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
                            >

                              <option value="ABOVE">
                                Is Above
                              </option>

                              <option value="BELOW">
                                Is Below
                              </option>

                              <option value="CROSS_ABOVE">
                                Crosses Above
                              </option>

                              <option value="CROSS_BELOW">
                                Crosses Below
                              </option>

                            </select>

                          </div>

                          <div>

                            <label className="text-[10px] uppercase font-bold text-slate-500">
                              Compared With
                            </label>

                            <select
                              value={ec.slow}
                              onChange={e => {

                                const next =
                                  [...optionEmaConditions];

                                next[i] = {
                                  ...next[i],
                                  slow: Number(e.target.value)
                                };

                                setOptionEmaConditions(next);

                              }}
                              className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
                            >

                              {[5,9,13,20,21,34,50,55,100,200].map(v => (
                                <option key={v} value={v}>
                                  EMA {v}
                                </option>
                              ))}

                            </select>

                          </div>

                          <button
                            type="button"
                            onClick={() => {

                              setOptionEmaConditions(
                                optionEmaConditions.filter(
                                  (_, j) => j !== i
                                )
                              );

                            }}
                            className="h-10 px-3 border border-red-200 text-red-500 text-xs"
                          >
                            Remove
                          </button>

                        </div>

                      ))}

                    </div>

                    {/* OPTION TOUCH */}

                    <div className="mt-5 border border-amber-200 bg-amber-50 p-4">

                      <div className="flex items-center gap-3">

                        <input
                          type="checkbox"
                          checked={optionEmaTouchEnabled}
                          onChange={e =>
                            setOptionEmaTouchEnabled(
                              e.target.checked
                            )
                          }
                        />

                        <div>

                          <div className="font-semibold text-sm">
                            Entry on Touch of Option EMA
                          </div>

                          <div className="text-xs text-slate-500 mt-1">
                            Entry is triggered when the selected
                            option premium touches the selected EMA.
                          </div>

                        </div>

                      </div>

                      {optionEmaTouchEnabled && (

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">

                          <div>

                            <label className="text-[10px] uppercase font-bold text-slate-500">
                              Select EMA
                            </label>

                            <select
                              value={optionEmaTouchPeriod}
                              onChange={e =>
                                setOptionEmaTouchPeriod(
                                  Number(e.target.value)
                                )
                              }
                              className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
                            >

                              {[5,9,13,20,21,34,50,55,100,200].map(v => (
                                <option key={v} value={v}>
                                  EMA {v}
                                </option>
                              ))}

                            </select>

                          </div>

                          <div>

                            <label className="text-[10px] uppercase font-bold text-slate-500">
                              Touch Direction
                            </label>

                            <select
                              value={optionEmaTouchDirection}
                              onChange={e =>
                                setOptionEmaTouchDirection(
                                  e.target.value
                                )
                              }
                              className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
                            >

                              <option value="BOTH">
                                Touch from Either Side
                              </option>

                              <option value="FROM_ABOVE">
                                Price Touching EMA from Above
                              </option>

                              <option value="FROM_BELOW">
                                Price Touching EMA from Below
                              </option>

                            </select>

                          </div>

                        </div>

                      )}

                    </div>

                  </div>

                )}

              </div>

            </div>
          </section>



          <section className="p-6 bg-white border-b border-slate-200">
            <div className="mb-4">
              <h3 className="font-heading font-bold mb-1">
                4. Option / Contract Selection
              </h3>
              <p className="text-xs text-slate-500">
                Configure how the option contract is selected at the strategy entry.
              </p>
            </div>

            {/* =========================================================
                 4. OPTION / CONTRACT SELECTION
               ========================================================= */}
             <div className="mb-4 px-1">
               <div className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">
                 4. Option / Contract Selection
               </div>
               <div className="text-xs text-slate-500 mt-1">
                 Select the option contract dynamically from the configured market conditions.
               </div>
             </div>
            <div className="mt-5 border border-emerald-200 bg-white">

              <div className="px-5 py-4 border-b border-emerald-200 bg-emerald-50">

                <div className="font-heading font-bold text-base">
                  Option Price Selection
                </div>

                <div className="text-xs text-slate-500 mt-1">
                  Select the historical option contract using the underlying index condition at the actual entry time.
                </div>

              </div>

              <div className="p-5">

                <div className="flex items-center gap-3">

                  <input
                    type="checkbox"
                    checked={dynamicOption.enabled}
                    onChange={e =>
                      setDynamicOption({
                        ...dynamicOption,
                        enabled: e.target.checked
                      })
                    }
                  />

                  <div>
                    <div className="font-semibold text-sm">
                      Select option dynamically from index condition
                    </div>

                    <div className="text-xs text-slate-500 mt-1">
                      When enabled, the backtester resolves the option contract from the historical index price and premium at the signal time.
                    </div>
                  </div>

                </div>

                {dynamicOption.enabled && (

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">

                    <div>
                      <label className="text-[10px] uppercase font-bold text-slate-500">
                        Option Type
                      </label>

                      <select
                        value={dynamicOption.option_type}
                        onChange={e =>
                          setDynamicOption({
                            ...dynamicOption,
                            option_type: e.target.value
                          })
                        }
                        className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
                      >
                        <option value="CE">CE</option>
                        <option value="PE">PE</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase font-bold text-slate-500">
                        Strike Basis
                      </label>

                      <select
                        value={dynamicOption.strike_basis}
                        onChange={e =>
                          setDynamicOption({
                            ...dynamicOption,
                            strike_basis: e.target.value
                          })
                        }
                        className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
                      >
                        <option value="ATM">ATM</option>

                        {[1,2,3,4,5,6,7,8,9,10].map(v => (
                          <React.Fragment key={v}>
                            <option value={`ITM${v}`}>
                              ITM {v}
                            </option>

                            <option value={`OTM${v}`}>
                              OTM {v}
                            </option>
                          </React.Fragment>
                        ))}

                      </select>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase font-bold text-slate-500">
                        Expiry
                      </label>

                      <select
                        value={dynamicOption.expiry}
                        onChange={e =>
                          setDynamicOption({
                            ...dynamicOption,
                            expiry: e.target.value
                          })
                        }
                        className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
                      >
                        <option value="Weekly">Weekly</option>
                        <option value="Monthly">Monthly</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase font-bold text-slate-500">
                        Price Selection
                      </label>

                      <select
                        value={dynamicOption.selection_mode}
                        onChange={e =>
                          setDynamicOption({
                            ...dynamicOption,
                            selection_mode: e.target.value
                          })
                        }
                        className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
                      >
                        <option value="Specific Premium">
                          Specific Premium
                        </option>

                        <option value="Premium Range">
                          Premium Range
                        </option>

                        <option value="ATM/Strike Basis">
                          ATM / Strike Basis
                        </option>
                      </select>
                    </div>

                    {dynamicOption.selection_mode === "Specific Premium" && (

                      <div>
                        <label className="text-[10px] uppercase font-bold text-slate-500">
                          Target Premium ₹
                        </label>

                        <input
                          type="number"
                          value={dynamicOption.premium}
                          onChange={e =>
                            setDynamicOption({
                              ...dynamicOption,
                              premium: e.target.value
                            })
                          }
                          placeholder="400"
                          className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm"
                        />
                      </div>

                    )}

                    {dynamicOption.selection_mode === "Premium Range" && (
                      <>
                        <div>
                          <label className="text-[10px] uppercase font-bold text-slate-500">
                            Minimum Premium ₹
                          </label>

                          <input
                            type="number"
                            value={dynamicOption.premium_min}
                            onChange={e =>
                              setDynamicOption({
                                ...dynamicOption,
                                premium_min: e.target.value
                              })
                            }
                            placeholder="400"
                            className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm"
                          />
                        </div>

                        <div>
                          <label className="text-[10px] uppercase font-bold text-slate-500">
                            Maximum Premium ₹
                          </label>

                          <input
                            type="number"
                            value={dynamicOption.premium_max}
                            onChange={e =>
                              setDynamicOption({
                                ...dynamicOption,
                                premium_max: e.target.value
                              })
                            }
                            placeholder="450"
                            className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm"
                          />
                        </div>
                      </>
                    )}

                    <div>
                      <label className="text-[10px] uppercase font-bold text-slate-500">
                        Lots
                      </label>

                      <input
                        type="number"
                        min="1"
                        value={dynamicOption.lots}
                        onChange={e =>
                          setDynamicOption({
                            ...dynamicOption,
                            lots: e.target.value
                          })
                        }
                        className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm"
                      />
                    </div>

                  </div>
                )}

              </div>
            </div>
          </section>


          {/* Positions */}
          <section className="p-6 border-b border-slate-200 bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold">5. Positions / Legs</h3>
              <button data-testid="add-position" onClick={() => setPositions([...positions, emptyPosition()])}
                className="text-xs font-semibold text-blue-600 flex items-center gap-1 hover:underline"><Plus className="w-3 h-3" /> Add Leg</button>
            </div>
            <div className="space-y-3">
              {positions.map((p, i) => (
                <div key={i} className="border border-slate-200 p-3" data-testid={`position-${i}`}>
                  <div className="grid grid-cols-2 md:grid-cols-7 gap-2 items-end">
                    <div><label className={LBL}>Segment</label>
                      <select value={p.segment} onChange={(e) => setPos(i, "segment", e.target.value)} className={`${SEL} mt-1`}>
                        {meta.segments.map((s) => <option key={s}>{s}</option>)}
                      </select></div>
                    <div><label className={LBL}>Type</label>
                      <select value={p.option_type} onChange={(e) => setPos(i, "option_type", e.target.value)} className={`${SEL} mt-1`}>
                        <option>CE</option><option>PE</option><option value="-">-</option>
                      </select></div>
                    <div><label className={LBL}>B/S</label>
                      <select value={p.side} onChange={(e) => setPos(i, "side", e.target.value)}
                        className={`${SEL} mt-1 ${p.side === "BUY" ? "text-emerald-600" : "text-rose-600"} font-semibold`}>
                        <option value="BUY">BUY</option><option value="SELL">SELL</option>
                      </select></div>
                    <div><label className={LBL}>Strike</label>
                      <select data-testid={`strike-${i}`} value={p.strike_selection} onChange={(e) => setPos(i, "strike_selection", e.target.value)} className={`${SEL} mt-1`}>
                        {meta.strike_selections.map((s) => <option key={s.id} value={s.id}>{s.id}</option>)}
                      </select></div>
                    <div><label className={LBL}>{showPremium(p.strike_selection) ? "Premium" : "Value"}</label>
                      <input value={showPremium(p.strike_selection) ? p.premium : p.strike_value}
                        onChange={(e) => setPos(i, showPremium(p.strike_selection) ? "premium" : "strike_value", e.target.value)}
                        className="mt-1 w-full h-10 px-2 font-mono bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600" placeholder="—" /></div>
                    <div><label className={LBL}>Expiry</label>
                      <select value={p.expiry} onChange={(e) => setPos(i, "expiry", e.target.value)} className={`${SEL} mt-1`}>
                        {meta.expiries.map((s) => <option key={s}>{s}</option>)}
                      </select></div>
                    <div className="flex items-end gap-2">
                      <div className="flex-1"><label className={LBL}>Lots</label>
                        <input type="number" min={1} value={p.lots} onChange={(e) => setPos(i, "lots", e.target.value)}
                          className="mt-1 w-full h-10 px-2 font-mono bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600" /></div>
                      {<button onClick={() => setPositions(positions.filter((_, idx) => idx !== i))}
                        className="h-10 w-9 flex items-center justify-center text-rose-500 hover:bg-rose-50 border border-slate-300"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                    <div><label className={LBL}>Target</label>
                      <select value={p.target_type} onChange={(e) => setPos(i, "target_type", e.target.value)} className={`${SEL} mt-1`}>
                        <option>None</option><option>% of Entry</option><option>Points</option>
                      </select></div>
                    <div><label className={LBL}>Target Val</label>
                      <input value={p.target_value} onChange={(e) => setPos(i, "target_value", e.target.value)}
                        className="mt-1 w-full h-10 px-2 font-mono bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600" /></div>
                    <div><label className={LBL}>Stoploss</label>
                      <select value={p.sl_type} onChange={(e) => setPos(i, "sl_type", e.target.value)} className={`${SEL} mt-1`}>
                        <option>None</option><option>% of Entry</option><option>Points</option>
                      </select></div>
                    <div><label className={LBL}>SL Val</label>
                      <input value={p.sl_value} onChange={(e) => setPos(i, "sl_value", e.target.value)}
                        className="mt-1 w-full h-10 px-2 font-mono bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600" /></div>
                  </div>
                </div>
              ))}
            </div>
          </section>


          <section className="p-6 bg-white border-b border-slate-200">
            <div className="mb-4">
              <h3 className="font-heading font-bold mb-1">
                6. Risk Management & Exit
              </h3>
              <p className="text-xs text-slate-500">
                Configure exit timing, profit target and stop-loss limits.
              </p>
            </div>

            <div>
              <div className="text-[11px] font-bold uppercase tracking-wide text-rose-700 mb-3">
                6. Risk Management
              </div>
              <h3 className="font-heading font-bold mb-1">7. Exit & Risk Management</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={LBL}>Exit Time</label>
                  <input
                    type="time"
                    value={exitTime}
                    onChange={(e) => setExitTime(e.target.value)}
                    disabled={exitOnExpiry}
                    className={`mt-1 w-full h-10 px-2 font-mono border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600 ${
                      exitOnExpiry ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-white"
                    }`}
                  />
                  {exitOnExpiry && (
                    <div className="text-[10px] text-amber-700 mt-1">
                      Ignored when Exit on Expiry is enabled.
                    </div>
                  )}
                </div>

                <div>
                  <label className={LBL}>Exit on Expiry</label>
                  <label className="mt-1 h-10 px-3 flex items-center gap-2 border border-slate-300 rounded-sm text-sm bg-white cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!exitOnExpiry}
                      onChange={(e) => setExitOnExpiry(e.target.checked)}
                    />
                    <span>{exitOnExpiry ? "Yes" : "No"}</span>
                  </label>
                  {exitOnExpiry && (
                    <div className="text-[10px] text-amber-700 mt-1">
                      Expiry exit has priority over Exit Time.
                    </div>
                  )}
                </div>

                <div><label className={LBL}>Profit MTM (₹)</label>
                  <input value={profitMtm} onChange={(e) => setProfitMtm(e.target.value)} placeholder="None"
                    className="mt-1 w-full h-10 px-2 font-mono bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600" /></div>
                <div><label className={LBL}>Stoploss MTM (₹)</label>
                  <input value={slMtm} onChange={(e) => setSlMtm(e.target.value)} placeholder="None"
                    className="mt-1 w-full h-10 px-2 font-mono bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-600" /></div>
              </div>
            </div>
          </section>


        </div>

        {/* Right rail */}
        <aside className="bg-slate-50 p-6 space-y-6">
          <div className="bg-white border border-slate-200 p-4">
            <div className="text-xs font-bold tracking-wide uppercase text-slate-500 mb-1">{underlying} Spot</div>
            {quote ? (
              <>
                <div className="font-mono text-3xl font-medium">{quote.ltp.toLocaleString("en-IN")}</div>
                <div className={`font-mono text-sm ${quote.change >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                  {quote.change >= 0 ? "+" : ""}{quote.change} ({quote.change_pct}%)
                </div>
                <div className="text-xs text-slate-400 mt-1 font-mono">Lot size {quote.lot_size}</div>
              </>
            ) : <div className="text-slate-400 text-sm">—</div>}
          </div>

          <div className="bg-white border border-slate-200 p-4">
            <div className="text-xs font-bold tracking-wide uppercase text-slate-500 mb-3">
              8. Review & Summary
            </div>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Legs</dt><dd className="font-mono">{positions.length}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Indicators</dt><dd className="font-mono">{indicators.length}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Sentiment</dt><dd className="font-mono">{sentiment}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Capital</dt><dd className="font-mono">₹{Number(capital).toLocaleString("en-IN")}</dd></div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Window</dt>
                <dd className="font-mono">
                  {entryTime}–{exitOnExpiry ? "EXPIRY" : exitTime}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Exit on Expiry</dt>
                <dd className="font-mono">{exitOnExpiry ? "YES" : "NO"}</dd>
              </div>
            </dl>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">
            Save to run a backtest over 6M–5Y of NSE history, or deploy to paper / live auto-trading.
          </p>
        </aside>
      </div>
    </Layout>
  );
}
